import { useEffect, useRef, useState } from "react";
import type { EmblaCarouselType } from "embla-carousel";
import {
  Anchor,
  Badge,
  Box,
  Flex,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  IconBuildingSkyscraper,
  IconChartBar,
  IconChartLine,
  IconExternalLink,
  IconGlobe,
  IconNews,
  IconUsers,
  IconWorld,
  type TablerIcon,
} from "@tabler/icons-react";
import CountUp from "react-countup";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import type { SectorEcosystemOverview } from "../../../../../../shared/sectorEcosystemOverview";
import type { SectorVisualConfig } from "../sectorEcosystem.const";
import {
  getStaticSectorCompanyStats,
  getStaticSectorCountryCount,
  getStaticSectorSuccessStories,
  getStaticSectorSuccessStoriesTotal,
} from "../sectorEcosystem.const";

type MetricDef = {
  label: string;
  value?: number;
  comingSoon?: boolean;
  icon: TablerIcon;
  color: string;
  bg: string;
};

function AnimatedCount({ value, countKey }: { value: number; countKey: string }) {
  return (
    <Text fw={700} fz="lg" lh={1.1} component="span" display="block">
      <CountUp key={countKey} start={0} end={value} duration={1.4} suffix="+" />
    </Text>
  );
}

const SUCCESS_STORY_TILES = [
  {
    key: "introductions" as const,
    label: "Introductions",
    icon: IconChartLine,
    bg: "#DCFCE7",
    color: "#166534",
  },
  {
    key: "businessMeetings" as const,
    label: "Business Meetings",
    icon: IconChartBar,
    bg: "#DBEAFE",
    color: "#1D4ED8",
  },
  {
    key: "crossBorderConnections" as const,
    label: "Cross Border Connections",
    icon: IconWorld,
    bg: "#EDE9FE",
    color: "#6D28D9",
  },
];

type SectorNewsItem = {
  id: string;
  title: string;
  summary: string;
  sourceUrl: string;
  publishedAt: Date | null;
  createdAt: Date;
};

const NEWS_AUTOPLAY_DELAY_MS = 5000;
const NEWS_CARD_WIDTH = 280;
const NEWS_CARD_HEIGHT = 220;

function NewsSlideBody({ item, accent }: { item: SectorNewsItem; accent: string }) {
  return (
    <Stack
      gap={8}
      px="sm"
      pt={10}
      pb={6}
      h="100%"
      justify="space-between"
      style={{ backgroundColor: "#FFFFFF", overflow: "hidden" }}
    >
      <Stack gap={8} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Text fw={700} size="sm" c="#0F172A" lh={1.35} lineClamp={2}>
          {item.title}
        </Text>
        <Text size="xs" c="#64748B" lh={1.5} ta="justify" lineClamp={5}>
          {item.summary}
        </Text>
      </Stack>
      <Group justify="flex-end" mt={2} style={{ flexShrink: 0 }}>
        <Anchor
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          fw={600}
          c={accent}
        >
          View sources <IconExternalLink size={11} style={{ verticalAlign: "middle" }} />
        </Anchor>
      </Group>
    </Stack>
  );
}

type SectorEcosystemHeroProps = {
  slug: string;
  overview: SectorEcosystemOverview;
  visual: SectorVisualConfig;
};

export default function SectorEcosystemHero({ slug, overview, visual }: SectorEcosystemHeroProps) {
  const SectorIcon = visual.icon;
  const [newsItems, setNewsItems] = useState<SectorNewsItem[]>([]);
  const [newsEmbla, setNewsEmbla] = useState<EmblaCarouselType | null>(null);
  const [activeNewsIndex, setActiveNewsIndex] = useState(0);
  const newsAutoplay = useRef(
    Autoplay({ delay: NEWS_AUTOPLAY_DELAY_MS, stopOnInteraction: false, stopOnMouseEnter: true }),
  );

  useEffect(() => {
    let cancelled = false;

    const loadNews = async () => {
      try {
        const res = await trpc.discover.listSectorNews.query({ sectorSlug: slug, limit: 8 });
        if (!cancelled) {
          setNewsItems(res.items as SectorNewsItem[]);
        }
      } catch (err) {
        if (!cancelled) {
          handleTrpcError(err);
          setNewsItems([]);
        }
      }
    };

    void loadNews();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    setActiveNewsIndex(0);
    newsEmbla?.scrollTo(0, true);
  }, [newsItems, newsEmbla]);

  useEffect(() => {
    if (!newsEmbla) return;

    const onSelect = () => setActiveNewsIndex(newsEmbla.selectedScrollSnap());

    onSelect();
    newsEmbla.on("select", onSelect);
    newsEmbla.on("reInit", onSelect);

    return () => {
      newsEmbla.off("select", onSelect);
      newsEmbla.off("reInit", onSelect);
    };
  }, [newsEmbla]);

  const hasNews = newsItems.length > 0;
  const multiNews = newsItems.length > 1;

  const companyStats = getStaticSectorCompanyStats(slug);
  const successStories = getStaticSectorSuccessStories(slug);
  const successStoriesTotal = getStaticSectorSuccessStoriesTotal(slug);
  const metrics: MetricDef[] = [
    {
      label: "Companies",
      value: companyStats.total,
      icon: IconBuildingSkyscraper,
      color: "#2563EB",
      bg: "#EFF6FF",
    },
    {
      label: "Ecosystem Heads",
      comingSoon: true,
      icon: IconUsers,
      color: "#7C3AED",
      bg: "#F5F3FF",
    },
    {
      label: "Countries",
      value: getStaticSectorCountryCount(slug),
      icon: IconGlobe,
      color: "#16A34A",
      bg: "#F0FDF4",
    },
    {
      label: "Success Stories",
      value: successStoriesTotal,
      icon: IconChartBar,
      color: "#F97316",
      bg: "#FFEDD5",
    },
  ];

  return (
    <Paper
      px={{ base: "md", sm: "xl" }}
      pt={15}
      py={15}
      style={{
        borderTopLeftRadius: 'var(--mantine-radius-md)',
        borderTopRightRadius: 'var(--mantine-radius-md)',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        position: "relative",
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
           <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="xl"
      >
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="sm" align="center" wrap="wrap">
            <ThemeIcon size={40} radius="md" style={{ backgroundColor: visual.iconBg, color: visual.iconColor }}>
              <SectorIcon size={22} />
            </ThemeIcon>
            <Title order={1} fz={{ base: 15, sm: 24 }} fw={500} c="#0F172A" lh={1} mt={0}>
              {overview.title}
            </Title>
            <Badge color="green" variant="light" size="xl" radius="sm">
              Active
            </Badge>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <Paper key={metric.label} p="sm" radius="md" withBorder style={{ borderColor: "#E2E8F0" }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" style={{ backgroundColor: metric.bg, color: metric.color }}>
                      <Icon size={16} />
                    </ThemeIcon>
                    <Box>
                      {metric.comingSoon ? (
                        <Text fw={700} fz="sm" c="#64748B" lh={1.2}>
                          Coming Soon
                        </Text>
                      ) : (
                        <AnimatedCount value={metric.value ?? 0} countKey={`${slug}-${metric.label}`} />
                      )}
                      <Text size="xs" c="dimmed">
                        {metric.label}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
            {SUCCESS_STORY_TILES.map((tile) => {
              const Icon = tile.icon;
              const value = successStories[tile.key];
              return (
                <Paper key={tile.key} p="sm" radius="md" withBorder style={{ borderColor: "#E2E8F0" }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size={32} radius="md" style={{ backgroundColor: tile.bg, color: tile.color }}>
                      <Icon size={16} />
                    </ThemeIcon>
                    <Box>
                      <AnimatedCount value={value} countKey={`${slug}-${tile.key}`} />
                      <Text size="xs" c="dimmed">
                        {tile.label}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
        <Box
          visibleFrom="md"
          w={NEWS_CARD_WIDTH}
          miw={NEWS_CARD_WIDTH}
          h={hasNews ? NEWS_CARD_HEIGHT : undefined}
          mih={hasNews ? NEWS_CARD_HEIGHT : 180}
          style={{
            flexShrink: 0,
            alignSelf: hasNews ? "flex-start" : undefined,
            borderRadius: 12,
            border: "1px solid #E2E8F0",
            backgroundColor: hasNews ? "#FFFFFF" : visual.iconBg,
            display: "flex",
            alignItems: hasNews ? "stretch" : "center",
            justifyContent: hasNews ? "flex-start" : "center",
            overflow: "hidden",
            boxShadow: hasNews ? "0 8px 24px rgba(15, 23, 42, 0.08)" : undefined,
          }}
        >
          {hasNews ? (
            <Stack gap={0} w="100%" h="100%">
              <Box
                px={10}
                py={8}
                style={{
                  background: `linear-gradient(135deg, ${visual.accent} 0%, #04305c 100%)`,
                }}
              >
                <Group justify="space-between" align="center"  wrap="nowrap" gap="xs">
                  <Stack gap={3} style={{ minWidth: 0, flex: 1 }}>
                    <Group gap={6} wrap="nowrap">
                      <Box
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 999,
                          backgroundColor: "#FFFFFF",
                          flexShrink: 0,
                        }}
                      />
                      <Text fw={700} c="#FFFFFF" tt="uppercase" lh={1}>
                        News feed
                      </Text>
                    </Group>
                  </Stack>
                  <IconNews size={18} color="rgba(255,255,255,0.88)" style={{ flexShrink: 0 }} />
                </Group>
              </Box>

              <Box style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                {multiNews ? (
                  <Carousel
                    slideSize="100%"
                    slideGap={0}
                    align="start"
                    withControls={false}
                    loop
                    getEmblaApi={setNewsEmbla}
                    plugins={[newsAutoplay.current]}
                    onMouseEnter={newsAutoplay.current.stop}
                    onMouseLeave={newsAutoplay.current.reset}
                    styles={{
                      root: { width: "100%", height: "100%" },
                      viewport: { height: "100%" },
                      container: { height: "100%" },
                      slide: { height: "100%" },
                    }}
                  >
                    {newsItems.map((item) => (
                      <Carousel.Slide key={item.id}>
                        <NewsSlideBody item={item} accent={visual.accent} />
                      </Carousel.Slide>
                    ))}
                  </Carousel>
                ) : (
                  <NewsSlideBody item={newsItems[0]} accent={visual.accent} />
                )}
              </Box>

              {multiNews ? (
                <Group justify="center" gap={6} py={4} style={{ flexShrink: 0 }}>
                  {newsItems.map((item, index) => {
                    const isActive = index === activeNewsIndex;
                    return (
                      <UnstyledButton
                        key={item.id}
                        type="button"
                        aria-label={`Go to news ${index + 1}`}
                        aria-current={isActive ? "true" : undefined}
                        onClick={() => newsEmbla?.scrollTo(index)}
                        style={{
                          width: isActive ? 8 : 6,
                          height: isActive ? 8 : 6,
                          borderRadius: 999,
                          backgroundColor: isActive ? visual.accent : "#CBD5E1",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          transition: "background-color 150ms ease, width 150ms ease, height 150ms ease",
                        }}
                      />
                    );
                  })}
                </Group>
              ) : null}
            </Stack>
          ) : (
            <SectorIcon size={96} color={visual.iconColor} style={{ opacity: 0.35 }} />
          )}
        </Box>
      </Flex>
    </Paper>
  );
}
